import type { ComponentType, ReactNode } from 'react';
import { CalendarPlus, Fingerprint, LogIn, Mail, ShieldCheck, ShieldOff, UsersRound } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { StaffCapabilities, StaffMemberSummary } from '../../types/staffManager';
import { ClickToReveal } from '../ClickToReveal';
import { SkeletonLoader } from '../SkeletonLoader';
import { TeamColourChip } from './manage/TeamColourChip';
import { MembershipExpiryBadge } from './manage/MembershipExpiryBadge';
import { TesseractAccessNotice } from './manage/TesseractAccessNotice';
import { formatTenure, isMembershipActive } from '../../utils/staffTenure';
import { cn } from '../../utils/cn';

interface StaffIdentityStripProps {
  summary: StaffMemberSummary | null;
  loading: boolean;
  error?: string | null;
  capabilities?: StaffCapabilities | null;
  className?: string;
}

const Fact = ({ icon: Icon, label, value, sub, tone = 'text-accent-300 bg-accent-500/15' }: { icon: ComponentType<{ className?: string }>; label: string; value: ReactNode; sub?: ReactNode; tone?: string }) => (
  <div className="p-4 md:p-5 bg-[var(--glass-bg)] min-w-0">
    <dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-400">
      <span className={cn('inline-flex w-6 h-6 items-center justify-center rounded-md', tone)}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      {label}
    </dt>
    <dd className="mt-2 text-lg font-bold text-white leading-tight min-w-0 truncate">{value}</dd>
    {sub && <dd className="mt-1 text-xs text-glass-500 min-w-0 truncate">{sub}</dd>}
  </div>
);

const ago = (ms: number | null) => (ms ? formatDistanceToNow(new Date(ms), { addSuffix: true }) : '—');
const day = (ms: number | null) => (ms ? format(new Date(ms), 'd MMM yyyy') : '—');

/**
 * Tesseract identity facts for one staff member: signup date, staff tenure, last login, account
 * state, email (click to reveal) and team memberships with join dates and expiry.
 */
export const StaffIdentityStrip = ({ summary, loading, error, capabilities, className }: StaffIdentityStripProps) => {
  if (loading) {
    return (
      <div className={cn('glass-card p-4 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3', className)}>
        {Array.from({ length: 5 }, (_, i) => (
          <SkeletonLoader key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const entry = summary?.tesseract ?? null;
  if (!entry) {
    return (
      <TesseractAccessNotice
        capabilities={capabilities ?? null}
        needs="role.view"
        error={error ?? summary?.tesseract_error ?? (summary ? 'This Discord account is not linked to any Tesseract staff member, so signup date and tenure are unknown.' : null)}
        compact
        className={className}
      />
    );
  }

  const now = Date.now();
  const active = entry.teams.filter((t) => isMembershipActive(t, now));
  const inactive = entry.teams.filter((t) => !isMembershipActive(t, now));
  const staffSince = summary?.staff_since ?? null;

  return (
    <div className={cn('glass-card relative overflow-hidden', className)}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" aria-hidden="true" />
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-px bg-glass-200/[0.07]">
        <Fact icon={CalendarPlus} label="Signed up" value={day(entry.created_at)} sub={entry.created_at ? `${ago(entry.created_at)} · Tesseract account` : 'unknown'} />
        <Fact
          icon={UsersRound}
          label="Staff since"
          value={staffSince ? formatTenure(staffSince, now) : '—'}
          sub={staffSince ? `${day(staffSince)} · earliest active team` : 'no active team membership'}
          tone="text-green-300 bg-green-500/15"
        />
        <Fact icon={LogIn} label="Last login" value={entry.last_login ? ago(entry.last_login) : '—'} sub={entry.last_login ? `${day(entry.last_login)} · Tesseract` : 'never seen'} tone="text-blue-300 bg-blue-500/15" />
        <Fact
          icon={entry.disabled ? ShieldOff : ShieldCheck}
          label="Account"
          value={entry.disabled ? 'Disabled' : entry.email_verified ? 'Active' : 'Unverified'}
          sub={<span className="font-mono">@{entry.username}</span>}
          tone={entry.disabled ? 'text-red-300 bg-red-500/15' : entry.email_verified ? 'text-green-300 bg-green-500/15' : 'text-amber-300 bg-amber-500/15'}
        />
        <Fact
          icon={Mail}
          label="Email"
          value={entry.email ? <ClickToReveal value={entry.email} label="Reveal email" valueClassName="text-sm" /> : '—'}
          sub={<span className="font-mono">{entry.user_id}</span>}
          tone="text-purple-300 bg-purple-500/15"
        />
      </dl>
      <div className="border-t border-glass-200/10 px-4 md:px-5 py-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500 mr-1">
          <Fingerprint className="w-3.5 h-3.5" /> Teams
        </span>
        {active.length === 0 && <span className="text-xs text-glass-500">No active team memberships.</span>}
        {active.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1.5">
            <TeamColourChip team={t} size="md" showDomain title={t.assigned_at ? `Joined ${format(new Date(t.assigned_at), 'd MMM yyyy')} (${formatTenure(t.assigned_at, now)})` : 'Join date unknown'} />
            <MembershipExpiryBadge expiresAt={t.expires_at} />
          </span>
        ))}
        {inactive.length > 0 && (
          <span className="text-[11px] text-glass-500">
            · {inactive.length} expired: {inactive.map((t) => t.display_name).join(', ')}
          </span>
        )}
      </div>
    </div>
  );
};
